import React, { useEffect } from "react";
import PageShell from "../components/PageShell";
import { setMeta } from "../lib/seo";

export default function About() {
  useEffect(() => {
    setMeta({
      title: "About Culinera — Alberta Food Discovery & Trip Planning",
      description:
        "Culinera is an Alberta food discovery and road-trip planning site, helping you find great restaurants across the province and turn them into a trip. Learn how listings are chosen.",
      canonical: "https://culinera.ca/about",
    });
  }, []);

  return (
    <PageShell testid="about-page">
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 prose prose-gray">
        <h1 className="text-4xl font-bold text-gray-900 mb-6">About Culinera</h1>
        <p className="text-lg text-gray-700 mb-6">
          Culinera is an Alberta food discovery and road-trip planning site. It exists for one reason:
          Alberta has an outstanding food scene that is easy to miss if you don't know where to look —
          chef-led restaurants in Edmonton and Calgary, small-town bakeries and diners, farmers' markets,
          and dining streets worth planning a trip around. Culinera helps you find those places and turn them into a trip.
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-3">What Culinera does</h2>
        <ul className="list-disc pl-6 space-y-2 text-gray-700">
          <li><strong>A restaurant directory</strong> covering Alberta cities, towns, and dining streets, from Calgary and Edmonton to Banff, Canmore, Jasper, and smaller stops along the way.</li>
          <li><strong>A trip-planning wizard</strong> that builds a personalized food itinerary from your travel dates, party size, and food preferences.</li>
          <li><strong>The Culinera Food Journal</strong>, with longer guides and day-by-day food road trips written from real travel in the province.</li>
        </ul>

        <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-3">How listings are chosen</h2>
        <p className="text-gray-700 mb-4">
          Listings are chosen for food quality, local character, and trip value — not for payment. A restaurant
          cannot buy its way onto a list or into a recommendation. When a listing's details change (hours, menus,
          locations), we update it as we learn of the change, and we remove listings that have closed.
        </p>
        <p className="text-gray-700 mb-4">
          We aim for accuracy, but restaurant hours, menus, and prices change often. Always check with the restaurant
          before you travel, especially for seasonal or small-town spots.
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-3">Editorial standards</h2>
        <p className="text-gray-700 mb-4">
          Our guides are written to be useful, not to pad a page: specific places, real routes, and practical notes
          (when to book, what to expect, what to skip). If a recommendation is based on a visit, the guide says so;
          if it is based on a listing's reputation and public information, it does not pretend otherwise.
        </p>

        <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-3">Who runs Culinera</h2>
        <p className="text-gray-700 mb-4">
          Culinera is operated by Paul Carter. Questions, corrections, and listing updates go to{" "}
          <a className="text-teal-600 hover:underline" href="mailto:hello@culinera.ca">hello@culinera.ca</a> — we read every message.
        </p>

        <div className="mt-10 flex flex-wrap gap-3">
          <a href="/?view=wizard" data-testid="about-plan-cta" className="bg-teal-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors">Plan a Trip</a>
          <a href="/sample-itineraries" data-testid="about-samples-cta" className="border border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors">See a Sample Itinerary</a>
        </div>
      </article>
    </PageShell>
  );
}
